// =============================================================================
// domain/excursionExtension.ts — the shape of the post-exit run.
// -----------------------------------------------------------------------------
// The Cost of Premature Exit card answers "what did leaving early cost me". This
// one answers the question underneath it: **what does the move after my exit
// usually look like?** A median, a distribution, and the same cut by setup and by
// time of day — because "you leave $6,000 behind" is a number, and "your median
// exit is followed by another 1.2R, and 41% of them keep running" is a habit.
//
// ── 🚨 THIS READS A STORED WALK. NOTHING HERE FETCHES ANYTHING ──
//
// Walking the bars after an exit costs money — 1-second bars, per trade, from a
// metered vendor — and it happens in the old client behind its Recompute button.
// What it leaves behind is `extras.extensions[<window>]`, and that is the entire
// input here. A trade whose walk was never computed is ABSENT from the
// population; it never reads as a zero, and this client does not offer to
// compute one.
//
// ── 🚨 NEVER A RAW PEAK, AND THAT IS JOSH'S OWN HARD RULE ──
//
// A trade with no derivable stop has a walk that ran to the peak of the whole
// window with nothing to stop it — a run nobody was holding. `qualifiedExtLeg`
// is the single implementation of which leg may be credited, shared with the
// cost card so the two can never disagree about one walk.
//
// ── ⚠ THE STORED DOLLARS ARE PER CONTRACT. THESE ARE NOT ──
//
// `computeTradeExtension` wrote `dollars = points × $/point` and omitted
// quantity. The old client's Trade Extension card prints that figure straight,
// while its own Cost of Premature Exit card — one card above, describing the same
// walk — multiplies by size. On a two-lot trade they differ by exactly 2×. Size
// is applied here, so both cards in this client answer in the same money. PD-13.
//
// ── ⚠ NO WALL CLOCK, NO STORE (D26) ──
// =============================================================================

import { median } from './excursionStats';
import {
  qualifiedExtLeg,
  type ExtensionLeg, type ExtensionLens, type ExtensionTrade, type StoredExtension,
} from './leakExtension';

export type { ExtensionLens } from './leakExtension';

export interface ExtensionWindow {
  key: string;
  minutes: number;
  label: string;
}

/**
 * ⚠ `session` IS KEYED BY NAME, NOT BY ITS MINUTES, and the old client has a
 * comment about why: results written under `240` while every reader looked for
 * `session` computed, cost money, and never appeared. The key is the contract
 * between writer and reader — never the duration.
 */
export const EXTENSION_WINDOWS: readonly ExtensionWindow[] = [
  { key: '15', minutes: 15, label: '15m' },
  { key: '30', minutes: 30, label: '30m' },
  { key: '60', minutes: 60, label: '1h' },
  { key: 'session', minutes: 240, label: 'Session' },
];

export const DEFAULT_EXTENSION_WINDOW = '30';

/** R-normalised, or the raw point distance. */
export type ExtensionMode = 'r' | 'points';

/**
 * ⚠ STAMPED AT COMPUTE TIME AND READ BACK, never re-derived from today's row.
 * The old client classifies against the planned target and stop at the moment it
 * walks the bars; re-deriving here would relabel any trade whose target was
 * edited afterwards, and the filter chips would disagree with the walks they are
 * filtering. Whatever strings are stored are the strings shown.
 */
export const EXIT_REASONS = ['target', 'stop', 'time', 'discretionary'] as const;
export type ExitReasonKey = (typeof EXIT_REASONS)[number];

export const EXIT_REASON_LABELS: Record<ExitReasonKey, string> = {
  target: 'Target',
  stop: 'Stop',
  time: 'Time',
  discretionary: 'Discretionary',
};

/** What this card needs beyond the stored walk. */
export interface ExtensionCardTrade extends ExtensionTrade {
  patternId: string | null;
  /** Clock string. Only used to place the trade in a session phase. */
  timeOut: string | null;
}

export interface ExtensionQuery {
  window: string;
  lens: ExtensionLens;
  mode: ExtensionMode;
  /** `null` means every reason. */
  exitReason: ExitReasonKey | null;
}

export interface ExtensionBucket {
  label: string;
  lo: number;
  hi: number;
  count: number;
}

export interface ExtensionPatternRow {
  id: string;
  name: string;
  sample: number;
  medianValue: number | null;
  /** Share that kept running — over 1R in R mode, over zero in points. */
  keptPct: number;
}

export interface ExtensionPhaseRow {
  key: string;
  label: string;
  sample: number;
  medianValue: number | null;
}

export interface ExtensionProfile {
  /** Trades in scope carrying a stored walk for the SELECTED window. */
  computed: number;
  /** In scope with no stored walk for it. Stated, never mistaken for a zero. */
  withoutWalk: number;
  /** Per window key, how many trades in scope carry a walk. Drives the honesty copy. */
  windowCounts: Record<string, number>;
  /** Per exit reason, over `computed`. A reason with none is not offered. */
  reasonCounts: Record<string, number>;
  /** Survived the exit-reason filter AND produced a creditable run. */
  shown: number;
  /**
   * Had a walk, and the ACTIVE VIEW could not read it.
   *
   * ⚠ EVERY ONE OF THEM, not only the ones that also lack a stop on today's row.
   * The old client counts `no stop AND no value`, so a trade excluded for any
   * other reason vanishes off the card with nothing said about it. A trade that
   * disappears silently is how a total ends up describing half a book.
   */
  excluded: number;
  /**
   * Of those, how many the POINTS view would show.
   *
   * ⚠ Computed, not assumed. "Switch to Points to include them" is advice that
   * is either true or a waste of a click, and the only way to know is to try the
   * other view on the same trades.
   */
  excludedShowableInPoints: number;
  medianValue: number | null;
  /** ⚠ SIZE-AWARE. See the header: the stored figure is per contract. */
  medianDollars: number | null;
  dollarSample: number;
  goodCount: number;
  goodPct: number;
  /** Would have been stopped out somewhere inside the window. */
  stoppedFirstCount: number;
  stoppedFirstPct: number;
  /**
   * 🚨 IN BOTH. The two shares above OVERLAP, and nothing on the old client's
   * card says so — its own sentence reads "40% ran 1R or more **without hitting
   * your stop first**, while 67% would've stopped out before extending (not
   * counted)". Both halves are false: the walk terminates AT the stop, so a
   * stopped trade still contributes the run it made before it, and that run can
   * be well past a full unit of risk.
   *
   * ⚠ On Josh's own MES book: 59 trades ran ≥1R, 98 would have been stopped, and
   * **20 are in both** — out of 146. A reader who subtracts, as that sentence
   * invites, gets a number that describes nothing. PD-14.
   */
  goodAndStoppedCount: number;
  medianTargetValue: number | null;
  medianTargetDollars: number | null;
  targetSample: number;
  buckets: ExtensionBucket[];
  patternRows: ExtensionPatternRow[];
  phases: ExtensionPhaseRow[];
  /** Distinct instrument roots in `shown` — the points-mode blending caveat. */
  instruments: number;
}

/** 🚨 `== null` FIRST — `Number(null)` is 0. Same guard, same reason, as the one
 *  in `leakExtension`, where writing it the other way turned a null stored R into
 *  a real reading of zero and a null dollar figure into a real $0. */
const fin = (v: unknown): number | null => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const walkOf = (t: ExtensionCardTrade, window: string): StoredExtension | null => {
  const e = t.extensions?.[window];
  return (e && typeof e === 'object') ? e : null;
};

/** A walk exists for this window when it carries a point distance. */
const hasWalk = (t: ExtensionCardTrade, window: string): boolean =>
  fin(walkOf(t, window)?.points) != null;

/** Whether a real stop was derivable — the same two sources the walk itself used. */
export function hasPlannedStop(t: ExtensionCardTrade): boolean {
  const ps = fin(t.plannedStop);
  const rp = fin(t.riskPoints);
  return (ps != null && ps > 0) || (rp != null && rp > 0);
}

/**
 * The creditable run under the active view, or null.
 *
 * ⚠ R MODE READS THE STORED `rMultiple`, it does not divide points by risk. The
 * stored figure was computed against the risk the trade carried at walk time, and
 * re-deriving it from today's row would quietly restate an old trade whose stop
 * was edited since.
 */
export function extensionValue(
  t: ExtensionCardTrade, q: ExtensionQuery,
): { value: number; leg: ExtensionLeg } | null {
  const leg = qualifiedExtLeg(walkOf(t, q.window), q.lens);
  if (!leg) return null;
  const v = fin(q.mode === 'points' ? leg.points : leg.rMultiple);
  return v == null ? null : { value: v, leg };
}

/**
 * Nice-round signed buckets for points mode.
 *
 * ⚠ DERIVED, because points have no universal scale — an ES point is not a CL
 * point, and a fixed ladder would flatten one instrument's whole distribution
 * into a single bar. The step comes off the 95th percentile of the magnitudes so
 * one outlier cannot set it. Three negative buckets and four positive, matching
 * the fixed R ladder, so the red/green split lands in the same place on both.
 */
export function niceExtensionBuckets(values: readonly number[]): { label: string; lo: number; hi: number }[] {
  const abs = values.map((v) => Math.abs(v)).filter((v) => Number.isFinite(v)).sort((a, b) => a - b);
  const p95 = abs.length ? (abs[Math.max(0, Math.floor((abs.length - 1) * 0.95))] as number) : 1;
  const ladder = [0.05, 0.1, 0.25, 0.5, 1, 2, 5, 10, 25, 50, 100, 250, 500, 1000];
  const raw = (p95 || 1) / 3;
  const s = ladder.find((x) => x >= raw) ?? (ladder[ladder.length - 1] as number);
  const f = (n: number): string => String(Math.round(n * 100) / 100);
  return [
    { label: `< -${f(2 * s)}`, lo: -Infinity, hi: -2 * s },
    { label: `-${f(2 * s)} to -${f(s)}`, lo: -2 * s, hi: -s },
    { label: `-${f(s)} to 0`, lo: -s, hi: 0 },
    { label: `0 to ${f(s)}`, lo: 0, hi: s },
    { label: `${f(s)} to ${f(2 * s)}`, lo: s, hi: 2 * s },
    { label: `${f(2 * s)} to ${f(3 * s)}`, lo: 2 * s, hi: 3 * s },
    { label: `> ${f(3 * s)}`, lo: 3 * s, hi: Infinity },
  ];
}

const R_BUCKETS = [
  { label: '< -2R', lo: -Infinity, hi: -2 },
  { label: '-2 to -1R', lo: -2, hi: -1 },
  { label: '-1 to 0', lo: -1, hi: 0 },
  { label: '0 to 1R', lo: 0, hi: 1 },
  { label: '1 to 2R', lo: 1, hi: 2 },
  { label: '2 to 3R', lo: 2, hi: 3 },
  { label: '> 3R', lo: 3, hi: Infinity },
];

/** ⚠ The first four buckets of both ladders are negative or zero-crossing; index 3 up is favourable. */
export const EXTENSION_POSITIVE_FROM = 3;

export interface SessionPhase { key: string; label: string; from: number; to: number }

/** Exit time of day, in the old client's four phases. Hours are decimal, ET. */
export const SESSION_PHASES: readonly SessionPhase[] = [
  { key: 'open', label: 'Open 9:30–11', from: 9.5, to: 11 },
  { key: 'mid', label: 'Midday 11–14', from: 11, to: 14 },
  { key: 'close', label: 'Close 14–16', from: 14, to: 16 },
  // ⚠ The wrap-around one. Everything outside the cash session, on both sides.
  { key: 'eth', label: 'Overnight / ETH', from: 16, to: 9.5 },
];

/** Decimal hour from a `HH:MM` clock string, or null. */
export function hourOfClock(clock: string | null): number | null {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(clock ?? ''));
  if (!m) return null;
  const h = Number(m[1]); const mins = Number(m[2]);
  if (!Number.isFinite(h) || !Number.isFinite(mins)) return null;
  return h + mins / 60;
}

export function phaseOfHour(h: number): SessionPhase | null {
  for (const p of SESSION_PHASES) {
    // ⚠ The overnight phase WRAPS. `from > to` means "outside [to, from)", and
    // testing it the ordinary way would put every after-hours exit nowhere.
    if (p.from > p.to ? (h >= p.from || h < p.to) : (h >= p.from && h < p.to)) return p;
  }
  return null;
}

/** Below this a pattern row is shown but marked as too small to act on. */
export const EXTENSION_PATTERN_MIN = 5;

/**
 * The whole card, from the stored walks.
 *
 * ⚠ EVERY FIGURE READS THE SAME `shown` SET. The magnitude, the dollars, the
 * histogram and the protective share all describe one population — the trades
 * that survived the exit-reason filter AND produced a creditable run under the
 * active lens. Computing the dollar context over a wider set than the median is
 * how a card ends up with a headline that no row in its own table supports.
 */
export function extensionProfile(
  trades: readonly ExtensionCardTrade[], q: ExtensionQuery, nameOf: (id: string) => string | null,
): ExtensionProfile {
  const windowCounts: Record<string, number> = {};
  for (const w of EXTENSION_WINDOWS) windowCounts[w.key] = 0;
  for (const t of trades) {
    for (const w of EXTENSION_WINDOWS) {
      if (hasWalk(t, w.key)) windowCounts[w.key] = (windowCounts[w.key] ?? 0) + 1;
    }
  }

  const computed = trades.filter((t) => hasWalk(t, q.window));

  const reasonCounts: Record<string, number> = {};
  for (const t of computed) {
    const r = String(walkOf(t, q.window)?.exitReason ?? '');
    if (r) reasonCounts[r] = (reasonCounts[r] ?? 0) + 1;
  }

  const filtered = q.exitReason == null
    ? computed
    : computed.filter((t) => walkOf(t, q.window)?.exitReason === q.exitReason);

  interface Shown { t: ExtensionCardTrade; value: number; dollars: number | null; stopped: boolean }
  const shown: Shown[] = [];
  let excluded = 0;
  let excludedShowableInPoints = 0;

  for (const t of filtered) {
    const v = extensionValue(t, q);
    if (!v) {
      excluded += 1;
      if (q.mode !== 'points' && extensionValue(t, { ...q, mode: 'points' })) {
        excludedShowableInPoints += 1;
      }
      continue;
    }
    const stored = fin(v.leg.dollars);
    // ⚠ SIZE APPLIED HERE. See the header — the stored figure is per contract,
    // and the cost card next door multiplies by size. A quantity that cannot be
    // read is treated as one lot rather than dropping the trade, matching the
    // cost card's own fallback.
    const qty = fin(t.qty) ?? 1;
    shown.push({
      t,
      value: v.value,
      dollars: stored == null ? null : stored * qty,
      stopped: v.leg.stoppedFirst === true,
    });
  }

  const values = shown.map((s) => s.value);
  const dollars = shown.map((s) => s.dollars).filter((d): d is number => d != null);

  // ⚠ "Good" means different things in the two modes, and it has to. In R a run
  // under a full unit of risk is not an opportunity you could have acted on; in
  // points there is no risk to compare against, so the only honest question is
  // whether it kept running at all.
  const good = q.mode === 'points'
    ? values.filter((v) => v > 0).length
    : values.filter((v) => v >= 1).length;

  const stoppedFirstCount = shown.filter((s) => s.stopped).length;
  // ⚠ THE OVERLAP, measured rather than assumed away. A trade can run a full
  // unit of risk your way and STILL be one the stop would have caught later —
  // the walk records the part before the stop, which is real money that was on
  // the table before it went. See `goodAndStoppedCount`.
  const goodAndStoppedCount = shown.filter(
    (s) => s.stopped && (q.mode === 'points' ? s.value > 0 : s.value >= 1),
  ).length;

  // ⚠ No guard on the active filter. When it is already a non-target reason this
  // is empty by construction, and a `q.exitReason === 'target' ? … : []` in front
  // of it could not change the answer — the kind of line that reads as the thing
  // producing the result and is trusted by the next person to touch it.
  const targets = shown.filter((s) => walkOf(s.t, q.window)?.exitReason === 'target');

  const ladder = q.mode === 'points' ? niceExtensionBuckets(values) : R_BUCKETS;
  const buckets: ExtensionBucket[] = ladder.map((b) => ({ ...b, count: 0 }));
  for (const v of values) {
    for (const b of buckets) {
      if (v >= b.lo && v < b.hi) { b.count += 1; break; }
    }
  }

  // ── by pattern ────────────────────────────────────────────────────────────
  const byPattern = new Map<string, { name: string; values: number[] }>();
  for (const s of shown) {
    const id = s.t.patternId;
    if (!id) continue;
    const name = nameOf(id);
    if (name == null) continue;
    const g = byPattern.get(id) ?? { name: name || '(Untitled)', values: [] };
    g.values.push(s.value);
    byPattern.set(id, g);
  }
  const patternRows: ExtensionPatternRow[] = [];
  for (const [id, g] of byPattern) {
    const kept = q.mode === 'points'
      ? g.values.filter((v) => v > 0).length
      : g.values.filter((v) => v >= 1).length;
    patternRows.push({
      id, name: g.name, sample: g.values.length,
      medianValue: median(g.values),
      keptPct: g.values.length ? (100 * kept) / g.values.length : 0,
    });
  }
  // ⚠ Small samples sink to the bottom rather than being dropped: a three-trade
  // pattern with a huge median is not a finding, but hiding it entirely makes
  // the table look like the trader has fewer setups than they do.
  patternRows.sort((a, b) => {
    const sa = a.sample < EXTENSION_PATTERN_MIN; const sb = b.sample < EXTENSION_PATTERN_MIN;
    if (sa !== sb) return sa ? 1 : -1;
    return (b.medianValue ?? -Infinity) - (a.medianValue ?? -Infinity);
  });

  // ── by session phase ──────────────────────────────────────────────────────
  const byPhase = new Map<string, number[]>();
  for (const s of shown) {
    const h = hourOfClock(s.t.timeOut);
    if (h == null) continue;
    const p = phaseOfHour(h);
    if (!p) continue;
    const arr = byPhase.get(p.key) ?? [];
    arr.push(s.value);
    byPhase.set(p.key, arr);
  }
  const phases: ExtensionPhaseRow[] = SESSION_PHASES
    .filter((p) => (byPhase.get(p.key)?.length ?? 0) > 0)
    .map((p) => {
      const arr = byPhase.get(p.key) as number[];
      return { key: p.key, label: p.label, sample: arr.length, medianValue: median(arr) };
    });

  // ⚠ DISTINCT CONTRACTS, which is what the instrument selector above the card
  // groups by — so the caveat and the dropdown can never disagree about how many
  // instruments are on screen. The old client instead takes the leading letters
  // of the symbol, intending to fold `MESZ5` and `MESH6` into one instrument; it
  // does not, because the month letter is a letter (`MESZ` vs `MESH`). Same
  // answer on a root symbol, and this one matches the control the user just used.
  const roots = new Set(
    shown.map((s) => String(s.t.contract ?? '').toUpperCase()).filter(Boolean),
  );

  return {
    computed: computed.length,
    withoutWalk: trades.length - computed.length,
    windowCounts,
    reasonCounts,
    shown: shown.length,
    excluded,
    excludedShowableInPoints,
    medianValue: median(values),
    medianDollars: median(dollars),
    dollarSample: dollars.length,
    goodCount: good,
    goodPct: values.length ? (100 * good) / values.length : 0,
    stoppedFirstCount,
    stoppedFirstPct: shown.length ? (100 * stoppedFirstCount) / shown.length : 0,
    goodAndStoppedCount,
    medianTargetValue: median(targets.map((s) => s.value)),
    medianTargetDollars: median(targets.map((s) => s.dollars).filter((d): d is number => d != null)),
    targetSample: targets.length,
    buckets,
    patternRows,
    phases,
    instruments: roots.size,
  };
}
